import {
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";

import { addApiTokenRequest } from "Api/apiToken/addApiToken.request";
import { deleteApiTokenRequest } from "Api/apiToken/deleteApiToken.request";
import { getApiTokensRequest } from "Api/apiToken/getApiTokens.request";
import logger from "Libs/logger";
import { setDeep } from "Libs/objectAccess";
import { normalize } from "Libs/utils";

import type { ApiToken } from "Api/apiToken/types";
import type { AsyncThunkOptionType } from "Reducers/types";
import type { RootState } from "Store/configureStore";

export type ApiTokenParams = {
  tokenId?: string;
  username: string;
  name?: string;
  userId?: string;
};

export const getApiTokens = createAsyncThunk<
  Record<string, ApiToken>,
  ApiTokenParams,
  AsyncThunkOptionType
>(
  "apiToken/list",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const tokens = await getApiTokensRequest(userId!);

      return normalize(tokens, "id");
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: ({ userId }) => {
      return !!userId;
    }
  }
);

export const addApiToken = createAsyncThunk<
  ApiToken,
  ApiTokenParams,
  AsyncThunkOptionType
>(
  "apiToken/add",
  async ({ name, userId }, { rejectWithValue }) => {
    try {
      return await addApiTokenRequest({ userId: userId!, name: name! });
    } catch (error) {
      const message = JSON.parse(error as string).message;
      logger(
        {
          message
        },
        {
          action: "addApiToken",
          meta: {
            userId
          }
        }
      );
      return rejectWithValue(error);
    }
  },
  {
    condition: ({ userId, name }) => {
      return !!userId && !!name;
    }
  }
);

export const apiTokenStateSelector = (state: RootState) => state.apiToken;

export const apiTokenSelector = createSelector(
  apiTokenStateSelector,
  (_: RootState, params: { username?: string; tokenId?: string }) => params,
  (apiTokenState, { username, tokenId }) =>
    username && tokenId && apiTokenState.data?.[username]?.[tokenId]
);

export const deleteApiToken = createAsyncThunk<
  ApiToken | undefined,
  ApiTokenParams,
  AsyncThunkOptionType
>(
  "apiToken/delete",
  async ({ tokenId, username, userId }, { getState, rejectWithValue }) => {
    try {
      const tokenToDelete = apiTokenSelector(getState(), { username, tokenId });

      if (!tokenToDelete) return;

      await deleteApiTokenRequest({
        userId: userId!,
        tokenId: tokenId!
      });

      return tokenToDelete;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: ({ userId, tokenId }) => {
      return !!userId && !!tokenId;
    }
  }
);

type ApiTokenState = {
  canAddNew?: boolean;
  currentlySavingToken?: boolean;
  data?: {
    [username: string]: { [tokenId: string]: ApiToken } | undefined;
  };
  errors?: unknown;
  loading?: boolean;
  newApiToken?: ApiToken;
};

const initialState: ApiTokenState = {};

const apiToken = createSlice({
  name: "apiToken",
  initialState,
  reducers: {
    expandNewApiTokenForm(state) {
      state.canAddNew = true;
    },
    cancelAddNew(state) {
      state.canAddNew = false;
      state.errors = null;
    },
    closeTokenBanner(state) {
      delete state.newApiToken;
    }
  },
  extraReducers: builder => {
    builder.addCase(getApiTokens.pending, state => {
      state.loading = true;
    });

    builder.addCase(getApiTokens.fulfilled, (state, action) => {
      const { username } = action.meta.arg;

      setDeep(state, ["data", username], action.payload);
      state.loading = false;
    });

    builder.addCase(getApiTokens.rejected, (state, action) => {
      state.errors = action.payload;
      state.loading = false;
    });

    (builder.addCase(addApiToken.pending, state => {
      state.currentlySavingToken = true;
      delete state.errors;
    }),
      builder.addCase(addApiToken.rejected, (state, action) => {
        state.currentlySavingToken = false;
        state.errors = action.error.message;
        state.loading = false;
      }),
      builder.addCase(addApiToken.fulfilled, (state, action) => {
        const { username } = action.meta.arg;
        const { id } = action.payload;

        setDeep(state, ["data", username, id], action.payload);
        state.newApiToken = action.payload;
        state.currentlySavingToken = false;
        state.canAddNew = false;
      }),
      builder.addCase(deleteApiToken.pending, state => {
        state.loading = true;
      }),
      builder.addCase(deleteApiToken.fulfilled, (state, action) => {
        if (!action.payload) return;

        const { username } = action.meta.arg;
        const { id } = action.payload;

        delete state.data?.[username]?.[id];
        state.loading = false;
      }),
      builder.addCase(deleteApiToken.rejected, (state, action) => {
        state.currentlySavingToken = false;
        state.errors = action.error.message;
        state.loading = false;
      }));
  }
});

export const { expandNewApiTokenForm, cancelAddNew, closeTokenBanner } =
  apiToken.actions;

export default apiToken.reducer;
